import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, Copy, KeyRound, Loader2, RotateCcw, XCircle } from "lucide-react";

/**
 * Il codice di visita, generato sul posto.
 *
 * Il preposto lo fa comparire, lo gira verso il visitatore e quello lo digita
 * sul proprio telefono. Per questo il numero e' grosso e nell'alfabeto non ci
 * sono lettere che si confondono: si legge ad alta voce in mezzo al rumore.
 *
 * Vale una volta sola e per pochi minuti. Se il preposto lo mostra alla persona
 * sbagliata, o cambia idea, c'e' "Annulla" prima che qualcuno lo usi.
 */
export default function CodiceVisita({ cantiereId, cantiereNome, variante = "solido" }) {
  const [aperto, setAperto] = useState(false);
  const [codice, setCodice] = useState(null);
  const [genero, setGenero] = useState(false);
  const [copiato, setCopiato] = useState(false);

  const genera = async () => {
    setGenero(true);
    try {
      const { data } = await api.post("/visite/codici", { cantiere_id: cantiereId });
      setCodice(data);
      setAperto(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setGenero(false); }
  };

  const annulla = async () => {
    if (!codice?.id) return;
    try {
      await api.post(`/visite/codici/${codice.id}/annulla`);
      toast.success("Codice annullato");
      setAperto(false); setCodice(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const copia = async () => {
    try {
      await navigator.clipboard.writeText(codice.codice);
      setCopiato(true);
      setTimeout(() => setCopiato(false), 1500);
    } catch {
      toast.error("Il telefono non ha lasciato copiare: leggilo da qui");
    }
  };

  const indirizzo = `${window.location.origin}/visita`;

  return (
    <>
      <Button onClick={genera} disabled={genero}
              data-testid={`genera-codice-${cantiereId}`}
              variant={variante === "contorno" ? "outline" : "default"}
              className={variante === "contorno"
                ? "h-11"
                : "h-11 bg-orange-500 hover:bg-orange-600 text-white"}>
        {genero ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <KeyRound className="w-4 h-4 mr-2" />}
        Genera password
      </Button>

      <Dialog open={aperto} onOpenChange={(v) => { if (!v) setAperto(false); }}>
        <DialogContent data-testid="codice-visita">
          <DialogHeader>
            <DialogTitle>Codice di visita</DialogTitle>
          </DialogHeader>
          {codice && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Leggilo al visitatore. Lo digita su{" "}
                <span className="font-medium text-slate-800">{indirizzo}</span> dal
                proprio telefono.
              </p>

              <div className="rounded-xl bg-[#0067b2] text-white py-6 text-center">
                <div className="text-xs uppercase tracking-[0.2em] opacity-80 mb-1">
                  {codice.cantiere_nome || cantiereNome}
                </div>
                <div className="font-mono text-5xl font-black tracking-[0.15em]">
                  {codice.codice}
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={copia} className="h-11 flex-1">
                  {copiato ? <Check className="w-4 h-4 mr-2" />
                           : <Copy className="w-4 h-4 mr-2" />}
                  Copia
                </Button>
                <Button variant="outline" onClick={genera} disabled={genero}
                        className="h-11">
                  <RotateCcw className="w-4 h-4 mr-2" /> Un altro
                </Button>
              </div>

              <div className="text-sm text-slate-600 space-y-1 border-t border-slate-100 pt-3">
                <p>
                  Vale <span className="font-medium">{codice.minuti_validita} minuti</span>{" "}
                  e una volta sola: appena qualcuno entra, si spegne.
                </p>
                <p>
                  Poi la visita si chiude da sola dopo{" "}
                  <span className="font-medium">{codice.minuti_inattivita} minuti</span>{" "}
                  senza attività. Se gli serve ancora tempo, gliene generi un altro.
                </p>
              </div>

              <Button variant="ghost" onClick={annulla}
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 h-10">
                <XCircle className="w-4 h-4 mr-2" /> Annulla questo codice
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
